import { MastercardEventData } from '@beat-venues/common-types';
import {
    EventCompactCard,
    Modal,
    Spinner,
    CheckboxSlider,
} from '@beat-venues/ui/react';
import { Container, Heading, Paragraph, Text } from '@beat-venues/ui/subset';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { usePreferencesStore } from '../../states/preferences-state';
import { useContext, useState } from 'react';
import { UserContext } from '../../context';
import { useNavigate } from 'react-router-dom';
import { getRecaptchaToken } from '../../utils';

export const Presales = () => {
    const { t, i18n } = useTranslation();
    const { preferences, setPreferences } = usePreferencesStore();
    const { user } = useContext(UserContext);
    const [showModal, setShowModal] = useState(
        preferences.length > 0 && !preferences.includes('NB_commercial')
    );
    const [isSubscribed, setIsSubscribed] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const navigate = useNavigate();

    const { isPending, data: events = [] } = useQuery<MastercardEventData[]>({
        queryKey: ['events-presales', i18n.language],
        queryFn: async () => {
            const result = await fetch(
                `/api/events/presale?lang=${i18n.language}`
            ).then((res) => {
                if (res.ok) {
                    return res.json();
                } else return [];
            });

            return result;
        },
        staleTime: 5 * 60 * 1000, // 5 minutes caching
    });

    const { isPending: isPendingPref } = useQuery({
        queryKey: ['get-preferences', user],
        queryFn: async () => {
            if (user && user.email && user.sfContactId) {
                const result = await fetch(
                    `/api/preferences/getPreferences/?email=${user?.email}&id=${user?.sfContactId}`
                );
                if (result.ok) {
                    setPreferences(await result.json());
                    if (!preferences?.includes('NB_commercial')) {
                        setShowModal(true);
                    }
                }
            }
            return '200 OK';
        },
        enabled: !preferences?.length, // Only fetch if preferences are not already set
    });

    const mutation = useMutation({
        mutationFn: async () => {
            const response = await fetch(`/api/preferences/optIn`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    email: user?.email,
                    preferences:
                        preferences && preferences?.length > 0
                            ? [...preferences, 'NB_commercial']
                            : ['NB_commercial'],
                    firstName: user?.firstName,
                    lastName: user?.lastName,
                    language: user?.language,
                    recaptchaToken: await getRecaptchaToken(),
                }),
            });

            if (!response.ok) {
                throw new Error(t('error.preferences') || '');
            } else {
                setIsSubscribed(true);
                setShowModal(false);
                setPreferences([...preferences, 'NB_commercial']);
            }

            return response.json();
        },
        onError: () => {
            setErrorMessage(t('error.preferences'));
        },
    });

    if (isPendingPref || (isPending && events.length === 0) || isPending) {
        return <Spinner className="mb-16" venueId="beat" />;
    }

    if (!preferences.includes('NB_commercial') && !isSubscribed) {
        return (
            <Modal
                isShown={showModal}
                onClose={() => undefined}
                className="h-auto md:h-fit"
            >
                <Modal.Header
                    className="text-xl"
                    onClose={() => {
                        setShowModal(false);
                        navigate('/');
                    }}
                >
                    {t('presales.popUp.title')}
                </Modal.Header>
                <Modal.Content>
                    <Text> {t('presales.popUp.descriptionTitle')}</Text>
                    <ul className="my-4">
                        {t('presales.popUp.descriptionBody')
                            .split('. ')
                            .map((item, index) => (
                                <li key={index} className="mb-1">
                                    ✔️ {item.trim()}
                                </li>
                            ))}
                    </ul>
                    <Text className="mb-4">
                        {t('presales.popUp.descriptionInfo1')}
                    </Text>
                    <hr className="mt-3 border-gray-100" />

                    {errorMessage && (
                        <Paragraph className="bg-danger-500 my-4 rounded-sm p-2 pl-4">
                            {errorMessage}
                        </Paragraph>
                    )}
                    <CheckboxSlider
                        checked={isSubscribed || mutation.isPending}
                        textColor="text-black"
                        label={t('preferences.other.commercialLabel')}
                        venueId="beat"
                        onChange={(e) => {
                            e.preventDefault();
                            mutation.mutate();
                        }}
                        disabled={mutation.isPending}
                    />
                </Modal.Content>
            </Modal>
        );
    }
    return (
        <Container className="mb-12">
            <div className="mb-8 px-4">
                <Heading as="h1" className="font-light xl:ml-0" size="h1">
                    {t('presales.title')}
                </Heading>
                <Text>{t('presales.description')}</Text>
            </div>
            <EventCompactCard.List>
                {events && events.length > 0 ? (
                    events.map((event) => (
                        <li key={event.id}>
                            <EventCompactCard.Item
                                ctaButton={event.statusCta}
                                ctaTextLink={{
                                    url: event.slug,
                                    label: t('events.ctaLabels.moreInfo'),
                                }}
                                date={event.dateFull ?? event.date}
                                eventId={event.id}
                                image={event.image}
                                name={event.name}
                                status={event.status}
                                venueId={event.venueId}
                                venueName={event.venueName}
                                endSalesDate={event.endSalesDate}
                            />
                        </li>
                    ))
                ) : (
                    <li>{t('presales.noPresales')}</li>
                )}
            </EventCompactCard.List>
        </Container>
    );
};
